import { default as Document } from '@tiptap/extension-document'
import { default as Text } from '@tiptap/extension-text'
import { Paragraph } from '@tiptap/extension-paragraph'
import { HardBreak } from '@tiptap/extension-hard-break'
import type { Extensions } from '@tiptap/react'
import { Placeholder } from '@tiptap/extension-placeholder'

import {
  CollaborationExtensionsOptions,
  getCollaborationExtensions,
} from '../collaboration'
import type { Editor } from '@tiptap/core'
import { SlashCommands } from './extension-slash-command'
import {
  type CommandListComponentProps,
  type SlashCommandType,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/slash-command'

export type CommentExtensionsOptions = {
  collaborationExtensionsOptions: CollaborationExtensionsOptions
  onPressEnter?: (params: { editor: Editor }) => boolean
  onPressArrowUp?: (params: { editor: Editor }) => boolean
  onPressArrowRight?: (params: { editor: Editor }) => boolean
  onPressArrowLeft?: (params: { editor: Editor }) => boolean
  onPressArrowDown?: (params: { editor: Editor }) => boolean
  onPressEscape?: (params: { editor: Editor }) => boolean
  onDeleteAtFirstChar?: (params: { editor: Editor }) => void | Promise<void>
  component: React.FC<CommandListComponentProps>
  onSlashCommandOpen: () => void
  onSlashCommandClose: () => void
  onSlashCommandSelectBlock: SlashCommandType['onSelectBlock']
  onFocus: (
    this: { name: string; editor: Editor },
    props: { event: FocusEvent }
  ) => boolean
}

// need to be in sync with commentExtensionsForTransformations (server side only transformations)
export const getCommentExtensions = (options: CommentExtensionsOptions) => {
  return [
    Placeholder.configure({
      placeholder: `Press / to add a block`,
    }),
    Document.configure(),
    Text.configure(),
    Paragraph.extend({
      onFocus: options.onFocus,
      addKeyboardShortcuts() {
        return {
          Enter({ editor }: { editor: Editor }) {
            if (!options.onPressEnter) return false
            return options.onPressEnter?.({ editor })
          },
          ArrowUp({ editor }: { editor: Editor }) {
            if (!options.onPressArrowUp) return false
            return options.onPressArrowUp({ editor })
          },
          ArrowDown({ editor }: { editor: Editor }) {
            if (!options.onPressArrowDown) return false
            return options.onPressArrowDown({ editor })
          },
          ArrowRight({ editor }: { editor: Editor }) {
            if (!options.onPressArrowRight) return false
            return options.onPressArrowRight({ editor })
          },
          ArrowLeft({ editor }: { editor: Editor }) {
            if (!options.onPressArrowLeft) return false
            return options.onPressArrowLeft({ editor })
          },
          Escape({ editor }: { editor: Editor }) {
            if (!options.onPressEscape) return false
            return options.onPressEscape({ editor })
          },
          Backspace({ editor }: { editor: Editor }) {
            if (!options.onDeleteAtFirstChar) return false
            const selection = editor.view.state.selection
            if (selection.empty && selection.from === 1 && selection.to === 1) {
              options.onDeleteAtFirstChar({ editor })
              return true
            }
            return false
          },
        }
      },
    }).configure(),
    HardBreak.configure(),
    SlashCommands.configure({
      component: options.component,
      onOpen: options.onSlashCommandOpen,
      onClose: options.onSlashCommandClose,
      onSelectBlock: options.onSlashCommandSelectBlock,
    }),
    ...getCollaborationExtensions(options.collaborationExtensionsOptions),
  ] satisfies Extensions
}
